import React, { useState, useEffect } from "react";
import get from "lodash/get";

// Components
import OptimizedImage from "../../player/OptimizedImage";
import PlayerHero from "../../player/PlayerHero";
import PerformanceIntel from "../../player/PerformanceIntel";
import ForensicAnalysis from "../../player/ForensicAnalysis";
import ArticlesSection from "../../player/ArticlesSection";
import MobileProfile from "../../player/MobileProfile";
import IdentityGate from "../../custom-widgets/IdentityGate";

// Hooks & Services
import { usePlayerStats } from "../../player/usePlayerStats";
import { getPositionalMetrics, formatComparisonItems } from "../../../../server/services/custom-pages/playerHelpers";

// Styles
import styles from "./PlayerProfile.module.css";

export function PlayerPage(props) {
    const data = get(props, ["data"], {});
    const { player, statscoreId, playerEntity, valueBenchmarks, initialStats, isMember: initialIsMember } = data;
    const [isMounted, setIsMounted] = useState(false);

    const {
        isMember,
        isSyncing,
        isRevealing,
        email,
        setEmail,
        emailError,
        setEmailError,
        isSubmitting,
        handleUnlock,
        stats
    } = usePlayerStats(initialStats, statscoreId, playerEntity, initialIsMember);

    useEffect(() => {
        setIsMounted(true);
        const scriptId = 'stn-player-script';
        if (!document.getElementById(scriptId)) {
            const script = document.createElement('script');
            script.id = scriptId;
            script.src = `https://embed.sendtonews.com/player3/embedcode.js?fk=${props.adId || "j1dzuhuw"}`;
            script.async = true;
            document.body.appendChild(script);
        }
    }, [props.adId]);

    if (!player) {
        return (
            <div className={styles["player-not-found"] || ""}>
                <h2>Player Not Found</h2>
            </div>
        );
    }

    const { positionStats, trendData, benchmarks, stories: desktopArticles, statscoreData } = stats;

    // Derived Data
    const clubName = statscoreData?.parent_participant?.name || positionStats?.parent_participant?.name || player.club;
    const clubLogo = Array.isArray(player.club_image) && player.club_image.length > 0
        ? player.club_image[0].url
        : (typeof player.club_image === "string" ? player.club_image : null);

    const positionalMetrics = getPositionalMetrics(player.position, positionStats);
    const activeBenchmarks = benchmarks && benchmarks.length > 0 ? benchmarks : valueBenchmarks;
    const comparisonValueItems = formatComparisonItems(player, activeBenchmarks, isMember, "value");
    const comparisonRatingItems = formatComparisonItems(player, activeBenchmarks, isMember, "rating", positionStats);
    const articlesList = desktopArticles || stats.stories || stats.articles || [];

    return (
        <div
            className={`custom-page-root ${styles["player-profile-container"]}`}
            style={{ "--player-frame-url": 'url("/player/frame.jpg")' }}
        >
            <PlayerHero
                player={player}
                clubName={clubName}
                clubLogo={clubLogo}
                playerPosition={player.position}
                statscoreData={statscoreData}
                positionStats={positionStats}
            />

            {/* STN Video Player */}
            {isMounted && (
                <section className={styles["player-video-section"]}>
                    <div className={styles["desktop-panel"]}>
                        <div data-stn-player={props.adId || "j1dzuhuw"} className={styles.videoPlayer}></div>
                    </div>
                </section>
            )}

            <div>
                {/* IdentityGate overlays the blurred content below — they share one position:relative wrapper */}
                <div style={{ position: "relative" }}>
                    <IdentityGate
                        isMember={isMember}
                        isRevealing={isRevealing}
                        email={email}
                        setEmail={setEmail}
                        emailError={emailError}
                        setEmailError={setEmailError}
                        isSubmitting={isSubmitting}
                        handleUnlock={handleUnlock}
                        isSyncing={isSyncing}
                    />

                    <main className={`${styles["desktop-profile-shell"]} ${styles["content-reveal"]} ${!isMember ? styles["blurred-content-locked"] : ""}`}>
                        <PerformanceIntel
                            player={player}
                            positionStats={positionStats}
                            positionalMetrics={positionalMetrics}
                            isMember={isMember}
                        />

                        <ForensicAnalysis
                            comparisonValueItems={comparisonValueItems}
                            comparisonRatingItems={comparisonRatingItems}
                            trendData={trendData}
                        />
                    </main>
                </div>

                <div className={styles["desktop-profile-shell"]}>
                    <ArticlesSection articles={articlesList} />
                </div>

                <MobileProfile
                    player={player}
                    isMember={isMember}
                    positionStats={positionStats}
                    positionalMetrics={positionalMetrics}
                    trendData={trendData}
                    comparisonValueItems={comparisonValueItems}
                    comparisonRatingItems={comparisonRatingItems}
                    articles={articlesList}
                />
            </div>
        </div>
    );
}

export default PlayerPage;
